/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Empty, Modal, Row, Select, Typography } from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
import EchartsWrapper from './EchartsWrapper';
import ChartLoader from './ChartLoader';

// Misc imports
import is from '../../utils/is';
import { truncate } from '../../utils/string/truncate';
import { CSAT_ANALYTICS_THRESHOLDS } from '../../constants/main';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import useTheme from '../../utils/hooks/useTheme';
import { formatNumberToThousandSeparator } from '../../utils/string/formatNumberToThousandSeparator';

// Constant declerations

const SORT_BY_OPTIONS = {
  RESPONSES: {
    label: 'Sort by Responses',
    value: 'responses'
  },
  SCORE: {
    label: 'Sort by Score',
    value: 'score'
  }
};

const CSATSectionsScoreSection = ({ data = [], sectionTitle = '', seriesName = '', isLoading = true, renderOnlyChart = false, renderOnlyTable = false }) => {
  const [chartToFullscreen, setChartToFullscreen] = useState();
  const [sortBy, setSortBy] = useState(SORT_BY_OPTIONS.RESPONSES.value);
  const { t } = useTranslation();
  const { successColorAnalytics: successColor, warningColorAnalytics: warningColor, errorColorAnalytics: errorColor, textColor, textColorSecondary } = useTheme();

  const getOptions = (chartData) => {

    const sortedData = [...chartData || []]?.sort((a, b) =>
      sortBy === SORT_BY_OPTIONS.RESPONSES.value
        ? (b.count - a.count) : (b.averageScorePCT - a.averageScorePCT)
    );

    return {
      tooltip: {
        show: true,
        trigger: 'item',
        className: 'ant-tooltip p-0 border-none text-wrap',
        formatter: (params) => {
          const score = sortedData[params.dataIndex]?.averageScore;
          const scorePCT = sortedData[params.dataIndex]?.averageScorePCT;
          const responseCount = sortedData[params.dataIndex]?.count;
          const title = sortedData[params.dataIndex]?.sectionTitle;

          let sentiment = CSAT_ANALYTICS_THRESHOLDS.NEGATIVE.label;
          let colorIndicator = errorColor;
          if (scorePCT >= CSAT_ANALYTICS_THRESHOLDS.POSITIVE.threshold) {
            sentiment = CSAT_ANALYTICS_THRESHOLDS.POSITIVE.label;
            colorIndicator = successColor;
          } else if (scorePCT >= CSAT_ANALYTICS_THRESHOLDS.NEUTRAL.threshold) {
            sentiment = CSAT_ANALYTICS_THRESHOLDS.NEUTRAL.label;
            colorIndicator = warningColor;
          }

          return `
              <div 
              class="d-flex flex-column border-radius-base shadow"
              style="width: 220px; max-width: 220px;"
              >
              <div 
              class="bg-layout text-center p-2 border-radius-top-left-base border-radius-top-right-base fw-600 text-wrap">
                ${title}
              </div>
              <div class="d-flex flex-column p-2 w-100">
                <div class="mb-1">
                <span>Rating Score:</span>
                <span class="fw-600">${scorePCT}% (${score}/5)</span>
                </div>
                <div class="d-flex my-1 align-items-center">
                  <span class="d-inline-block mr-1" style="width: 8px; height: 8px; border-radius: 50%; background-color: ${colorIndicator};"></span> 
                  <span">${sentiment}</span>
                </div>
                <div class="mb-1">Responses: ${formatNumberToThousandSeparator(responseCount)}</div>
              </div>
            </div>`;
        }

      },
      legend: {},
      grid: {
        left: '15%',
        right: '5%',
        top: '3%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        show: false,
        max: 100
      },
      yAxis: {
        type: 'category',
        data: sortedData.map((section) => section.sectionTitle),
        axisLabel: {
          show: true,
          formatter: (value, index) => {
            const section = sortedData?.find(section => section.sectionTitle === value);
            return `{section|${truncate(section.sectionTitle, 14, '...')}}\n{responses|Responses: ${section.count}}\n{score|Score: ${section.averageScore}/5}`;
          },
          width: 135,
          rich: {
            section: {
              fontSize: 14,
              fontWeight: 'bold',
              color: textColor,
              align: 'left'
            },
            responses: {
              fontSize: 12,
              fontWeight: 'bold',
              color: textColorSecondary,
              padding: [5, 0, 5, 0],
              align: 'left'
            },
            score: {
              fontSize: 12,
              fontWeight: 'bold',
              color: textColorSecondary,
              align: 'left'
            }
          }
        },
        axisTick: {
          show: false
        },
        axisLine: {
          show: false
        }
      },
      dataZoom: [
        {
          type: 'slider',
          yAxisIndex: 0,
          start: 0,
          end: (5 / sortedData.length) * 100,
          width: 4,
          borderColor: 'rgba(0, 0, 0, 0)',
          backgroundColor: 'rgba(240, 240, 240, 0.9)',
          fillerColor: 'rgba(160, 160, 160, 0.8)',
          handleIcon: 'M0,0 v8 h4 v-8 h-4 Z',
          handleSize: '100%',
          handleStyle: {
            color: 'rgba(160, 160, 160, 1)',
            borderColor: 'rgba(0, 0, 0, 0)'
          },
          minValueSpan: 4,
          maxValueSpan: 4,
          showDetail: false,
          showDataShadow: false,
          textStyle: {
            color: textColor
          },
          brushSelect: false,
          zoomLock: true
        }
      ],
      series: [
        {
          name: '',
          type: 'bar',
          label: {
            show: true,
            formatter: '{c}%',
            fontSize: 14,
            fontWeight: 'bold',
            color: textColor
          },
          itemStyle: {
            borderRadius: [4, 4, 4, 4],
            color: (params) => {
              const score = params.value;
              if (score >= CSAT_ANALYTICS_THRESHOLDS.POSITIVE.threshold) {
                return successColor;
              }
              if (score >= CSAT_ANALYTICS_THRESHOLDS.NEUTRAL.threshold) {
                return warningColor;
              }
              return errorColor;
            }
          },
          emphasis: {
            focus: 'series'
          },
          barWidth: 50,
          barCategoryGap: '20%',
          data: sortedData?.map(section => section.averageScorePCT)
        }
      ]
    };
  };

  const chart
    = (<Fragment>
      {
        !isEmptyArray(data)
          ? <Row>
            <Col xs={24} className="mb-3">
              <Select
                style={{ minWidth: '180px' }}
                defaultValue={SORT_BY_OPTIONS.RESPONSES.value}
                onSelect={(value) => {
                  setSortBy(value);
                }}
              >
                {
                  Object.entries(SORT_BY_OPTIONS).map(([key, item]) => (
                    <Select.Option
                      key={key}
                      value={item.value}
                    >
                      {item.label}
                    </Select.Option>
                  ))
                }
              </Select>
            </Col>
            <Col xs={24} className="d-flex justify-content-center align-items-center">
              <EchartsWrapper
                chartOptions={getOptions(data)}
                externalClassname="d-flex justify-content-center"
                style={{ height: 367, minHeight: '367px' }}
                loading={isLoading}
              />
            </Col>
          </Row>
          : <div className="d-flex content-fill flex-column h-100">
            <div className="mt-5 d-flex align-items-center mb-auto text-center flex-auto align-self-center">
              <Empty
                description={(
                  <Typography.Paragraph>
                    {t('No data available for this chart')}
                  </Typography.Paragraph>
                )}
              />
            </div>
          </div>

      }
    </Fragment>
    );

  return (
    <Fragment>
      {
        renderOnlyChart ? chart
          : <Fragment>
            <div className="mb-6 position-relative">
              <Typography.Title level={4} className="mb-6">
                <Typography.Text className="mr-2">{sectionTitle}</Typography.Text>
              </Typography.Title>

              {isLoading ? <ChartLoader /> : chart}

              <Button
                type="text"
                size="large"
                disabled={isLoading}
                icon={<FullscreenOutlined />}
                className="position-absolute top-0 right-0"
                onClick={() => setChartToFullscreen(chart)}
              />
            </div>

            <Modal
              open={is.truthy(chartToFullscreen)}
              onCancel={() => setChartToFullscreen()}
              destroyOnClose
              footer={null}
              centered
              style={{ maxWidth: 1000 }}
              width={'95%'}
            >
              <div className="p-5">
                {chart}
              </div>
            </Modal>
          </Fragment>

      }
    </Fragment>
  );
};

CSATSectionsScoreSection.propTypes = {
  data: PropTypes.object,
  sectionTitle: PropTypes.string,
  seriesName: PropTypes.string,
  isLoading: PropTypes.bool,
  renderOnlyChart: PropTypes.bool,
  renderOnlyTable: PropTypes.bool
};

export default CSATSectionsScoreSection;
